import { useState } from 'react';
import type { CanonicalProfile } from '@/features/profile/useCanonicalProfile';
import { workflowApi, pathValue, type JsonRecord } from '../workflowApi';

/** Additional source fields live inside the Academy profile, sharing its save operation. */
export function DoctoralProfileExtension({
  section,
  profile,
  setField,
  setSection,
  saveNow,
}: {
  section: string;
  profile: CanonicalProfile;
  setField: (path: string, value: unknown) => void;
  setSection: (key: string, value: unknown) => void;
  saveNow: () => Promise<boolean>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const upload = async (path: string, file: File) => {
    setBusy(true);
    try {
      if (!(await saveNow())) throw new Error('Сначала сохраните профиль');
      const before = await workflowApi.load('profile');
      const after = await workflowApi.upload('profile', before.revision, path, file);
      setField(path, pathValue(after.profile, path));
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка загрузки');
    } finally {
      setBusy(false);
    }
  };
  const download = async (file: JsonRecord) => {
    try {
      const result = await workflowApi.document(String(file.fileId), String(file.versionId));
      const url = URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename || String(file.name || 'document');
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка скачивания');
    }
  };
  const documentField = (path: string, label: string) => {
    const value = pathValue(profile, path);
    const records = (Array.isArray(value) ? value : value ? [value] : []) as JsonRecord[];
    return (
      <div key={path} className="profile-form-grid">
        <strong>{label}</strong>
        {records.map((r, i) => (
          <span key={i}>
            {typeof r === 'object' && r !== null && r.fileId ? (
              <button className="profile-btn" onClick={() => void download(r)}>
                {String(r.name || 'Скачать')}
              </button>
            ) : (
              <span>Документ требует загрузки</span>
            )}
          </span>
        ))}
        <label className="profile-btn primary">
          Загрузить
          <input
            type="file"
            disabled={busy}
            aria-label={`Загрузить: ${label}`}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) void upload(path, file);
            }}
          />
        </label>
      </div>
    );
  };
  const input = (path: string, label: string, type = 'text') => (
    <label className="profile-field" key={path}>
      <span>{label}</span>
      <input
        type={type}
        value={String(pathValue(profile, path) || '')}
        onChange={(e) => setField(path, e.target.value)}
      />
    </label>
  );
  const credentials = (profile.credentials || {}) as JsonRecord;
  return (
    <fieldset
      aria-label="Данные докторантуры"
      disabled={busy}
      style={{ border: 0, padding: 0, margin: 0 }}
    >
      {['employment', 'projects', 'achievements', 'documents'].includes(section) && (
        <label className="profile-check">
          <input
            type="checkbox"
            checked={pathValue(profile, `profileNotApplicable.${section}`) === true}
            onChange={(e) => setField(`profileNotApplicable.${section}`, e.target.checked)}
          />
          Нет записей для этого раздела. Условие применяется только к пустому реестру.
        </label>
      )}
      {error && <p role="alert">{error}</p>}
      {busy && <p role="status">Загрузка документа…</p>}
      {section === 'personal' && (
        <div className="profile-form-grid">
          {input('personal.birth', 'Дата рождения', 'date')}
          {input('personal.birthplace', 'Место рождения')}
          {input('personal.originType', 'Постоянное место проживания')}
        </div>
      )}
      {section === 'academic' && (
        <>
          <h3>Данные докторантуры</h3>
          <label className="profile-check">
            <input
              type="checkbox"
              checked={pathValue(profile, 'profileNotApplicable.credentials') === true}
              onChange={(e) => setField('profileNotApplicable.credentials', e.target.checked)}
            />
            Языковые сертификаты / курсы пока отсутствуют.
          </label>
          <div className="profile-form-grid">
            {[
              ['admission', 'Год поступления'],
              ['studyType', 'Форма обучения / финансирования'],
              ['program', 'Образовательная программа'],
              ['group', 'Группа'],
              ['studyLang', 'Язык обучения'],
            ].map(([key, label]) => input(`academic.${key}`, label))}
          </div>
          {documentField('credentials.cv.file', 'CV')}
          {(['languages', 'courses'] as const).map((root) => {
            const rows = (credentials[root] || []) as JsonRecord[];
            const fields =
              root === 'languages'
                ? [
                    ['language', 'Язык'],
                    ['name', 'Сертификат'],
                  ]
                : [['title', 'Название курса']];
            const change = (next: JsonRecord[]) =>
              setSection('credentials', { ...credentials, [root]: next });
            return (
              <div key={root}>
                <h3>
                  {root === 'languages' ? 'Языки и сертификаты' : 'Курсы и повышение квалификации'}
                </h3>
                {rows.map((row, i) => (
                  <article key={i} className="profile-edit-card">
                    <div className="profile-form-grid">
                      {fields.map(([key, label]) => (
                        <label className="profile-field" key={key}>
                          <span>{label}</span>
                          <input
                            value={String(row[key] || '')}
                            onChange={(e) =>
                              change(
                                rows.map((r, j) => (i === j ? { ...r, [key]: e.target.value } : r)),
                              )
                            }
                          />
                        </label>
                      ))}
                    </div>
                    {documentField(`credentials.${root}.${i}.file`, 'Подтверждающий документ')}
                    <button
                      className="profile-btn"
                      onClick={() => change(rows.filter((_, j) => j !== i))}
                    >
                      Удалить запись
                    </button>
                  </article>
                ))}
                <button className="profile-btn primary" onClick={() => change([...rows, {}])}>
                  Добавить запись
                </button>
              </div>
            );
          })}
        </>
      )}
      {['documents', 'achievements'].includes(section) &&
        ((profile[section] || []) as JsonRecord[]).map((r, i) =>
          documentField(`${section}.${i}.file`, String(r.title || 'Подтверждающий документ')),
        )}
    </fieldset>
  );
}
